import { useEffect, useState } from "react";
import { X, ShieldAlert, ExternalLink, Calendar, Globe, Building2, RefreshCw } from "lucide-react";
import { fetchRansomwareGroupDetails, type RansomwareGroupDetails } from "../../../api/status";
import { formatNumber, formatPercent } from "../../../utils/ransomwareUtils";
import { useNavigate } from "react-router-dom";

interface GroupDrawerProps {
  groupName: string | null;
  timeframe: string;
  onClose: () => void;
}

export default function GroupDrawer({ groupName, timeframe, onClose }: GroupDrawerProps) {
  const navigate = useNavigate();
  const [details, setDetails] = useState<RansomwareGroupDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadGroupDetails = (name: string, tf: string) => {
    setLoading(true);
    setError(null);

    fetchRansomwareGroupDetails(name, tf)
      .then((res) => {
        setDetails(res);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error("Failed to load group details:", err);
        const errorMsg =
          err?.response?.data?.detail ||
          err?.message ||
          `Unable to load intelligence details for ${name}.`;
        setError(errorMsg);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!groupName) {
      setDetails(null);
      setError(null);
      return;
    }

    loadGroupDetails(groupName, timeframe);
  }, [groupName, timeframe]);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!groupName) return null;

  const handleNavigateToFeed = () => {
    onClose();
    navigate(`/domain-monitor?search=${encodeURIComponent(groupName)}`);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        display: "flex",
        justifyContent: "flex-end",
        background: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          height: "100%",
          background: "var(--dash-card-bg, #0f172a)",
          borderLeft: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.12))",
          boxShadow: "-12px 0 40px rgba(0, 0, 0, 0.6)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
        className="rw-drawer-container"
      >
        {/* DRAWER HEADER */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.1))",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            background: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: "rgba(0, 102, 224, 0.15)",
                  color: "var(--dash-indigo, #6366F1)",
                  border: "1px solid rgba(0, 102, 224, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldAlert size={18} />
              </div>
              <h2
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 800,
                  color: "var(--dash-text-primary, #f8fafc)",
                  margin: 0,
                }}
              >
                {groupName}
              </h2>
            </div>
            <p
              style={{
                fontSize: "0.78rem",
                color: "var(--dash-text-muted, #94a3b8)",
                marginTop: 4,
                margin: 0,
              }}
            >
              Ransomware syndicate dossier & disclosure history
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 8,
              padding: 6,
              color: "var(--dash-text-muted, #94a3b8)",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            aria-label="Close drawer"
          >
            <X size={18} />
          </button>
        </div>

        {/* DRAWER CONTENT */}
        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 24, flex: 1 }}>
          {/* LOADING SKELETON STATE */}
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "20px 0" }}>
              <div style={{ height: 70, borderRadius: 10, background: "rgba(255, 255, 255, 0.05)", animation: "pulse 1.5s infinite" }} />
              <div style={{ height: 160, borderRadius: 10, background: "rgba(255, 255, 255, 0.05)", animation: "pulse 1.5s infinite" }} />
              <div style={{ height: 220, borderRadius: 10, background: "rgba(255, 255, 255, 0.05)", animation: "pulse 1.5s infinite" }} />
            </div>
          )}

          {/* ERROR STATE WITH WORKING RETRY */}
          {!loading && error && (
            <div
              style={{
                padding: "16px 20px",
                borderRadius: 10,
                background: "rgba(239, 68, 68, 0.08)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                color: "#ef4444",
                fontSize: "0.85rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontWeight: 700 }}>Failed to load details</span>
                <span style={{ fontSize: "0.78rem", opacity: 0.9 }}>{error}</span>
              </div>
              <button
                onClick={() => loadGroupDetails(groupName, timeframe)}
                style={{
                  background: "#ef4444",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                <RefreshCw size={12} /> Retry
              </button>
            </div>
          )}

          {/* DATA LOADED */}
          {!loading && !error && details && (
            <>
              {/* TOP DOSSIER BAR */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  padding: "16px 20px",
                  borderRadius: 10,
                  background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.03))",
                  border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
                }}
              >
                <div>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--dash-text-muted, #94a3b8)", textTransform: "uppercase" }}>
                    Total Disclosures
                  </div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#ef4444", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                    {formatNumber(details.total_attacks)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--dash-text-muted, #94a3b8)", textTransform: "uppercase" }}>
                    Targeted Sectors
                  </div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "var(--dash-text-primary, #f8fafc)", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                    {details.targeted_sectors.length}
                  </div>
                </div>
              </div>

              {/* TARGETED SECTORS BREAKDOWN */}
              <div>
                <h4 style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)", marginBottom: 12 }}>
                  Targeted Industry Sectors
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {details.targeted_sectors.map((sec) => (
                    <div key={sec.name}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, color: "var(--dash-text-primary, #f8fafc)" }}>{sec.name}</span>
                        <span style={{ color: "var(--dash-text-muted, #94a3b8)", fontVariantNumeric: "tabular-nums" }}>
                          {formatNumber(sec.count)} ({formatPercent(sec.percentage)})
                        </span>
                      </div>
                      <div style={{ width: "100%", height: 5, borderRadius: 3, background: "rgba(255, 255, 255, 0.06)", overflow: "hidden" }}>
                        <div style={{ width: `${Math.max(sec.percentage, 4)}%`, height: "100%", background: "var(--dash-indigo, #6366F1)", borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RECENT VICTIMS DISCLOSURES TABLE */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <h4 style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)", margin: 0 }}>
                    Recent Disclosures ({details.recent_victims.length})
                  </h4>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    maxHeight: 320,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {details.recent_victims.length === 0 ? (
                    <div style={{ fontSize: "0.8rem", color: "var(--dash-text-muted, #94a3b8)", padding: "20px 0", textAlign: "center" }}>
                      No recent victim disclosures logged in this window.
                    </div>
                  ) : (
                    details.recent_victims.map((v, idx) => (
                      <div
                        key={v.id || `${v.victim}-${idx}`}
                        style={{
                          padding: "12px",
                          borderRadius: 8,
                          background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.02))",
                          border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.06))",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)" }}>
                            {v.victim}
                          </span>
                          <span style={{ fontSize: "0.75rem", color: "var(--dash-text-muted, #94a3b8)", display: "flex", alignItems: "center", gap: 4 }}>
                            <Calendar size={12} /> {v.date}
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--dash-text-muted, #94a3b8)" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Building2 size={12} /> {v.sector}
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Globe size={12} /> {v.flag} {v.country}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* ACTION LINK TO INTELLIGENCE FEED */}
              <div style={{ marginTop: "auto", paddingTop: 16 }}>
                <button
                  onClick={handleNavigateToFeed}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: 8,
                    background: "var(--dash-indigo, #6366F1)",
                    color: "#ffffff",
                    border: "none",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: "0 4px 14px rgba(0, 102, 224, 0.3)",
                    transition: "all 0.15s ease",
                  }}
                >
                  <span>View in Intelligence Feed</span>
                  <ExternalLink size={14} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
